/**
 * Normalize backend listing responses to a common row shape for AdminListingTable.
 * Status: Available | Full | Closed | Completed
 */

function formatDate(d) {
  if (!d) return '—';
  const date = typeof d === 'string' ? new Date(d) : d;
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function computeStatusGroupShared(item) {
  const endDate = item.end_date ? new Date(item.end_date) : null;
  if (endDate && endDate < new Date() && item.fully_paid) return 'Completed';
  if (item.slot_closed === true) {
    const spots = item.spots_left;
    if (spots != null && spots <= 0) return 'Full';
    return 'Closed';
  }
  if (item.accepting_registrations && (item.spots_left == null || item.spots_left > 0)) return 'Available';
  if (item.spots_left != null && item.spots_left <= 0) return 'Full';
  return 'Closed';
}

/**
 * @param {Object} t - group_tour from API
 * @returns {Object} normalized row for AdminListingTable
 */
export function normalizeGroupTour(t) {
  if (!t) return null;
  const status = computeStatusGroupShared(t);
  const paid = t.paid_people_count ?? t.paid_interests_count ?? 0;
  const min = t.min_group_size ?? 0;
  const max = t.max_group_size;
  const spotsLeft = t.spots_left != null ? t.spots_left : (max != null ? Math.max(0, max - paid) : null);
  const price = t.final_price ?? t.vendor_total_price ?? t.total_amount;
  const cc = t.currency_code || 'NPR';
  const priceStr = price != null ? `${cc === 'USD' ? '$' : 'Rs '}${Number(price).toFixed(2)}` : '—';
  return {
    id: t.idx || t.id,
    title: t.name || '—',
    start_date: t.start_date,
    end_date: t.end_date,
    start_date_display: formatDate(t.start_date),
    end_date_display: formatDate(t.end_date),
    min_group_size: min,
    max_group_size: max ?? '—',
    paid_count: paid,
    spots_left: spotsLeft,
    accepting_registrations: t.accepting_registrations === true,
    published: t.published === true,
    status,
    vendor: t.vendor_company_name || (t.vendor_id ? `Vendor #${t.vendor_id}` : '—'),
    vendor_id: t.vendor_id,
    price: priceStr,
    price_raw: price,
    currency_code: cc,
    type: 'group',
    raw: t
  };
}

/**
 * @param {Object} r - shared_rental from API
 * @returns {Object} normalized row
 */
export function normalizeSharedRental(r) {
  if (!r) return null;
  const status = computeStatusGroupShared(r);
  const paid = r.paid_people_count ?? r.paid_interests_count ?? 0;
  const min = r.min_group_size ?? 0;
  const max = r.max_group_size;
  const spotsLeft = r.spots_left != null ? r.spots_left : (max != null ? Math.max(0, max - paid) : null);
  const price = r.final_price ?? r.vendor_total_price ?? r.total_amount;
  const cc = r.currency_code || 'NPR';
  const priceStr = price != null ? `${cc === 'USD' ? '$' : 'Rs '}${Number(price).toFixed(2)}` : '—';
  return {
    id: r.idx || r.id,
    title: r.name || '—',
    start_date: r.start_date,
    end_date: r.end_date,
    start_date_display: formatDate(r.start_date),
    end_date_display: formatDate(r.end_date),
    min_group_size: min,
    max_group_size: max ?? '—',
    paid_count: paid,
    spots_left: spotsLeft,
    accepting_registrations: r.accepting_registrations === true,
    published: r.published === true,
    status,
    vendor: r.vendor_company_name || (r.vendor_id ? `Vendor #${r.vendor_id}` : '—'),
    vendor_id: r.vendor_id,
    price: priceStr,
    price_raw: price,
    currency_code: cc,
    type: 'shared',
    raw: r
  };
}

/**
 * @param {Object} p - package (private tour) from API
 * @returns {Object} normalized row (no start/end dates or spots for private tours)
 */
export function normalizePackage(p) {
  if (!p) return null;
  const price = p.final_price ?? p.vendor_total_price ?? p.price;
  const cc = p.currency_code || 'NPR';
  const priceStr = price != null ? `${cc === 'USD' ? '$' : 'Rs '}${Number(price).toFixed(2)}` : '—';
  const status = p.published ? 'Available' : 'Closed';
  return {
    id: p.idx || p.id,
    title: p.name || '—',
    start_date: null,
    end_date: null,
    start_date_display: '—',
    end_date_display: '—',
    min_group_size: '—',
    max_group_size: '—',
    paid_count: '—',
    spots_left: '—',
    accepting_registrations: null,
    published: p.published === true,
    status,
    vendor: p.vendor_company_name || (p.vendor_id ? `Vendor #${p.vendor_id}` : '—'),
    vendor_id: p.vendor_id,
    price: priceStr,
    price_raw: price,
    currency_code: cc,
    type: 'private',
    raw: p
  };
}

export function normalizeList(items, type) {
  if (!Array.isArray(items)) return [];
  const fn = type === 'group' ? normalizeGroupTour : type === 'shared' ? normalizeSharedRental : normalizePackage;
  return items.map((item) => fn(item)).filter(Boolean);
}
